import {Alert, CircularProgress} from "@mui/material";
import {ZonedDateTime} from "@variocube/app-ui";
import React, {createContext, PropsWithChildren, useCallback, useContext, useMemo} from "react";
import {useAsync, useAsyncCallback} from "react-async-hook";
import {B2Booking, B2Occupancy} from "../data/client.gen";
import {useSiteApi} from "../data/sites";
import {getZoneDateTimeNow} from "../dates-utils";
import {isBlank, notBlank} from "../utils";

interface BookingContextProps {
	booking: B2Booking | undefined;
	bookingFrom: ZonedDateTime | undefined;
	bookingUntil: ZonedDateTime | undefined;
	isBookingActive: boolean | undefined;
	refresh: () => Promise<B2Booking | undefined>;
	occupy: (cubeId: string) => Promise<Array<B2Occupancy> | undefined>;
	changeBox: (occupancy: B2Occupancy) => Promise<B2Occupancy | undefined>;
	open: (occupancy: B2Occupancy) => Promise<void>;
	openBoxes: () => Promise<void>;
	release: () => Promise<void>;
	cancel: () => Promise<void>;
}

const BookingContext = createContext<BookingContextProps>({
	booking: {} as B2Booking,
	bookingFrom: undefined,
	bookingUntil: undefined,
	isBookingActive: undefined,
	refresh: () => {
		throw new Error("Context not properly initiated.");
	},
	occupy: () => {
		throw new Error("Context not properly initiated.");
	},
	changeBox: () => {
		throw new Error("Context not properly initiated.");
	},
	open: () => {
		throw new Error("Context not properly initiated.");
	},
	openBoxes: () => {
		throw new Error("Context not properly initiated.");
	},
	release: () => {
		throw new Error("Context not properly initiated.");
	},
	cancel: () => {
		throw new Error("Context not properly initiated.");
	},
});

function BookingContextProvider({children}: PropsWithChildren<{}>) {
	const {getBooking, occupyBoxes, changeBox, openBox, openBoxes, cancelBooking, releaseLockers} = useSiteApi();

	const {result: booking, loading, error, execute: refresh} = useAsync(
		async () => {
			try {
				const bookingId = window.location.pathname.split("/")[3];
				return (notBlank(bookingId))
					? getBooking(bookingId as string)
					: undefined;
			}
			catch (err) {
				console.error("Failed to load booking", err);
				return undefined;
			}
		},
		[],
		{
			// Keep the already-loaded booking visible while a refresh() is in flight. react-async-hook's default
			// setLoading resets `result` to undefined on every execute(), which would flip `booking` to undefined
			// mid-refresh — unmounting the whole booking view (children are gated on `booking !== undefined` below)
			// and remounting it when the refetch lands. That remount resets the auto-occupy `attempted` guard, so a
			// refresh triggered by an occupy that produced no occupancy (e.g. an offline cube) re-fires auto-occupy
			// in an endless loop, with the page visibly reloading each cycle (#1187). Preserving `result` keeps the
			// view mounted across refreshes.
			setLoading: (prev) => ({...prev, loading: true, status: "loading", error: undefined}),
		},
	);

	const {loading: occupyLoading, error: occupyError, execute: occupyExecute} = useAsyncCallback(occupyBoxes);
	const {error: changeBoxError, execute: changeBoxExecute} = useAsyncCallback(changeBox);
	const {error: openBoxesError, execute: openBoxesExecute} = useAsyncCallback(openBoxes);
	const {error: openError, execute: openExecute} = useAsyncCallback(openBox);
	const {loading: releaseLoading, error: releaseError, execute: releaseExecute} = useAsyncCallback(releaseLockers);
	const {loading: cancelLoading, error: cancelError, execute: cancelExecute} = useAsyncCallback(cancelBooking);

	const handleOccupyBoxes = useCallback(async (cubeId: string) => {
		let occupancies: B2Occupancy[] | undefined;
		if (notBlank(booking?.id)) {
			occupancies = await occupyExecute(booking?.id as string, cubeId);
			// Occupying changes server-computed booking fields (prolongableFrom, keepLockersUrl, endedOccupancies),
			// so refetch — otherwise a release + re-occupy keeps showing the stale post-release booking.
			await refresh();
		}
		return occupancies;
	}, [booking]);

	const handleChangeBox = useCallback(async (occupancy: B2Occupancy) => {
		if (notBlank(booking?.id)) {
			const changed = await changeBoxExecute(booking?.id as string, occupancy);
			// A box swap ends the old occupancy, so server-computed booking fields (prolongableFrom, keepLockersUrl,
			// endedOccupancies) change — refetch to keep them in sync, mirroring occupy/release/cancel.
			await refresh();
			return changed;
		}
		return undefined;
	}, [booking]);

	const handleOpenBox = useCallback(async (occupancy: B2Occupancy) => {
		if (notBlank(booking?.id)) {
			await openExecute(booking?.id as string, occupancy);
		}
	}, [booking]);

	const handleOpenBoxes = useCallback(async () => {
		if (notBlank(booking?.id)) {
			await openBoxesExecute(booking?.id as string);
		}
	}, [booking]);

	const handleCancelBooking = useCallback(async () => {
		if (notBlank(booking?.id)) {
			await cancelExecute(booking?.id as string);
			await refresh();
		}
	}, [booking]);

	const handleReleaseBooking = useCallback(async () => {
		if (notBlank(booking?.id)) {
			await releaseExecute(booking?.id as string);
			await refresh();
		}
	}, [booking]);

	const bookingFrom = useMemo(() => {
		if (isBlank(booking?.from) || isBlank(booking?.site.zoneId)) return undefined;
		return ZonedDateTime.from(`${booking?.from}[${booking?.site.zoneId}]`);
	}, [booking?.from, booking?.site.zoneId]);

	const bookingUntil = useMemo(() => {
		if (isBlank(booking?.until) || isBlank(booking?.site.zoneId)) return undefined;
		return ZonedDateTime.from(`${booking?.until}[${booking?.site.zoneId}]`);
	}, [booking?.until, booking?.site.zoneId]);

	/**
	 * Active booking is any bookings that has:
	 * - effective booking timeframe is within current timeframe
	 * - state equals to Confirmed
	 * - at least 1 occupancy
	 *
	 * Use this flag in a combination with other conditions to toggle the correspondence actions of the booking.
	 */
	const isBookingActive = useMemo(() => {
		const zoneId = booking?.site.zoneId ?? "UTC";
		const now = getZoneDateTimeNow(zoneId);

		// booking not existed or not yet started
		if (!booking || !bookingFrom || !bookingUntil || ZonedDateTime.compare(bookingFrom, now) > 0) {
			if (booking !== undefined) console.warn("Booking not yet started.");
			return false;
		}

		// booking was canceled and does not have at least an occupancy
		if (booking.state == "Canceled" && (booking.occupancies ?? []).length === 0) {
			console.warn("Booking was canceled and has no active occupancies.");
			return false;
		}

		if (booking.site.bookingUnit === "Days") {
			// grace period evaluation
			// allow access until the next `accessibleFrom` time independent of the tenant
			// if `accessibleFrom` is not set, default to 00:00
			const accessibleFrom = booking.site.accessibleFrom || "00:00";

			const [hour, minute] = accessibleFrom.split(":");
			try {
				const until = bookingUntil
					.add({days: 1})
					.with({hour: parseInt(hour), minute: parseInt(minute)});
				console.log("until", until.toJSON());
				return ZonedDateTime.compare(until, now) >= 0;
			}
			catch (err) {
				console.error("failed to parse grace period of until", err);
			}
		}

		return ZonedDateTime.compare(bookingFrom, now) <= 0
			&& ZonedDateTime.compare(bookingUntil, now) >= 0;
	}, [booking, bookingFrom, bookingUntil]);

	const value = useMemo(() => ({
		booking,
		bookingFrom,
		bookingUntil,
		isBookingActive,
		refresh,
		occupy: handleOccupyBoxes,
		changeBox: handleChangeBox,
		release: handleReleaseBooking,
		open: handleOpenBox,
		openBoxes: handleOpenBoxes,
		cancel: handleCancelBooking,
	}), [
		booking,
		bookingFrom,
		bookingUntil,
		isBookingActive,
		refresh,
		handleOccupyBoxes,
		handleChangeBox,
		handleReleaseBooking,
		handleOpenBox,
		handleOpenBoxes,
		handleCancelBooking,
	]);

	return (
		<BookingContext.Provider value={value}>
			{
				/* No global spinner for occupy/change-box (nor for the booking refresh occupy triggers): the booking
			    view's locker card renders its own inline progress, and a spinner popping in above the app shifts
			    the whole layout. The shared loading flag only spins for the initial booking load. */
			}
			{((loading && booking === undefined) || releaseLoading || cancelLoading) && <CircularProgress />}
			{error && <Alert severity="error">{error?.message ?? "Error loading booking"}</Alert>}
			{occupyError && <Alert severity="error">{occupyError?.message ?? "Error occupying boxes"}</Alert>}
			{changeBoxError && <Alert severity="error">{changeBoxError?.message ?? "Error changing box"}</Alert>}
			{openError && <Alert severity="error">{openError?.message ?? "Error opening box"}</Alert>}
			{openBoxesError && <Alert severity="error">{openBoxesError?.message ?? "Error opening all boxes"}</Alert>}
			{releaseError && <Alert severity="error">{releaseError?.message ?? "Error releasing lockers"}</Alert>}
			{cancelError && <Alert severity="error">{cancelError?.message ?? "Error cancel booking"}</Alert>}
			{booking !== undefined && (
				children
			)}
		</BookingContext.Provider>
	);
}

const useBooking = () => useContext(BookingContext);

export { BookingContextProvider, useBooking };
